// src/redux/selectors/riskFormGenerator.js
// Clean RTK-only selectors for risk form generation

import { createSelector } from 'reselect';
import { getRiskAreasFromTemplate, getRootCausesFromTemplate, getImpactCategoriesFromTemplate } from '../../utils/templateAdapter';

// ============================================================================
// Input Selectors - RTK only, no legacy state
// ============================================================================

const selectAssessmentsById = (state) => state.assessments?.byId || {};
const selectTemplatesById = (state) => state.templates?.byId || {};
const selectGlobalConfigs = (state) => state.globalConfigs?.configs || {};
const selectLegacyRiskSources = (state) => state.config?.configs?.riskSources;
const selectLegacyRiskRootCauses = (state) => state.config?.configs?.riskRootCauses;

// ============================================================================
// Factory Selector: createRiskFormSelector(assessmentUuid)
// Returns risk form taxonomy for a specific assessment based on its template
// ============================================================================

/**
 * Creates a selector that generates the risk form structure for a specific assessment.
 * Looks up the assessment's template_id and derives risk areas from the template.
 *
 * @param {string} assessmentUuid - The assessment UUID
 * @returns {Object} Risk form structure: { categoryName: { riskSources: [...] }, ... }
 */
export const createRiskFormSelector = (assessmentUuid) => createSelector(
  [selectAssessmentsById, selectTemplatesById, selectLegacyRiskSources, selectLegacyRiskRootCauses],
  (assessments, templates, legacyRiskSources, legacyRiskRootCauses) => {
    // Get the assessment
    const assessment = assessments[assessmentUuid];
    if (!assessment) {
      console.warn('[RiskFormGenerator] Assessment not found:', assessmentUuid);
      return {};
    }

    // Get template_id from assessment
    const templateId = assessment.info?.template_id;
    const template = templateId ? templates[templateId] : null;

    let finalRiskSources;
    let finalRiskRootCauses;

    if (template) {
      // Use template data
      finalRiskSources = getRiskAreasFromTemplate(template);
      finalRiskRootCauses = getRootCausesFromTemplate(template);
      console.log('[RiskFormGenerator] Using template:', {
        assessmentUuid,
        templateId,
        templateKeys: Object.keys(template),
        hasTaxonomy: !!template.taxonomy,
        taxonomyKeys: template.taxonomy ? Object.keys(template.taxonomy) : [],
        riskAreasKeys: finalRiskSources ? Object.keys(finalRiskSources) : [],
        areasCount: finalRiskSources ? Object.keys(finalRiskSources).length : 0,
        rootCausesKeys: finalRiskRootCauses ? Object.keys(finalRiskRootCauses) : []
      });
    } else {
      // Fallback to legacy PlanetScale configs
      finalRiskSources = legacyRiskSources;
      finalRiskRootCauses = legacyRiskRootCauses;
      console.log('[RiskFormGenerator] No template, using legacy config:', {
        assessmentUuid,
        templateId: templateId || 'none'
      });
    }

    if (!finalRiskSources || !finalRiskRootCauses) {
      console.warn('[RiskFormGenerator] No risk sources or root causes available');
      return {};
    }

    // Build the risk form structure
    const riskForm = {};
    Object.keys(finalRiskSources).forEach((category) => {
      const sources = finalRiskSources[category]?.riskSources;
      if (!sources || !Array.isArray(sources)) {
        console.warn(`[RiskFormGenerator] Invalid sources for category "${category}"`);
        return;
      }

      riskForm[category] = {
        riskSources: sources.map((source) => ({
          ...source,
          causes: { ...finalRiskRootCauses },
        })),
      };
    });

    return riskForm;
  }
);

// ============================================================================
// Factory Selector: createImpactCategoriesSelector(assessmentUuid)
// Returns impact categories for risk evaluation based on assessment's template
// ============================================================================

/**
 * Creates a selector for impact categories (formerly "themes") for a specific assessment.
 *
 * @param {string} assessmentUuid - The assessment UUID
 * @returns {Array} Impact categories: [{ theme_id, name, description }, ...]
 */
export const createImpactCategoriesSelector = (assessmentUuid) => createSelector(
  [selectAssessmentsById, selectTemplatesById, selectGlobalConfigs],
  (assessments, templates, globalConfigs) => {
    const legacyThemes = globalConfigs?.legacy_risk_themes || [];

    // Get the assessment
    const assessment = assessments[assessmentUuid];
    if (!assessment) {
      console.log('[ImpactCategories] Assessment not found, using legacy themes');
      return legacyThemes;
    }

    // Get template
    const templateId = assessment.info?.template_id;
    const template = templateId ? templates[templateId] : null;

    if (template) {
      const categories = getImpactCategoriesFromTemplate(template, legacyThemes);
      console.log('[ImpactCategories] Using template categories:', {
        assessmentUuid,
        templateId,
        count: categories.length
      });
      return categories;
    }

    console.log('[ImpactCategories] No template, using legacy themes');
    return legacyThemes;
  }
);

// ============================================================================
// Factory Selector: createEvaluationCriteriaSelector(assessmentUuid, criteriaType)
// Returns evaluation criteria (risk or control) from assessment's template
// ============================================================================

/**
 * Creates a selector for evaluation criteria for a specific assessment.
 * Fetches criteria from template.taxonomy.riskEvaluationCriteria or controlEvaluationCriteria.
 *
 * @param {string} assessmentUuid - The assessment UUID
 * @param {'risk' | 'control'} criteriaType - Type of criteria to fetch
 * @returns {Array} Evaluation criteria array
 */
export const createEvaluationCriteriaSelector = (assessmentUuid, criteriaType) => createSelector(
  [selectAssessmentsById, selectTemplatesById],
  (assessments, templates) => {
    // Get the assessment
    const assessment = assessments[assessmentUuid];
    if (!assessment) {
      console.log(`[EvaluationCriteria] Assessment not found: ${assessmentUuid}`);
      return [];
    }

    // Get template
    const templateId = assessment.info?.template_id;
    const template = templateId ? templates[templateId] : null;

    if (!template) {
      console.log(`[EvaluationCriteria] No template found for assessment: ${assessmentUuid}`);
      return [];
    }

    // Get the appropriate criteria from template
    let criteria;
    if (criteriaType === 'risk') {
      criteria = template.taxonomy?.riskEvaluationCriteria;
    } else if (criteriaType === 'control') {
      criteria = template.taxonomy?.controlEvaluationCriteria;
    } else if (criteriaType === 'cause') {
      criteria = template.taxonomy?.causeEvaluationCriteria;
    }

    if (!criteria) {
      console.log(`[EvaluationCriteria] No ${criteriaType} criteria in template:`, templateId);
      return criteriaType === 'cause' ? {} : [];
    }

    console.log(`[EvaluationCriteria] Using template ${criteriaType} criteria:`, {
      assessmentUuid,
      templateId,
      count: Array.isArray(criteria) ? criteria.length : Object.keys(criteria).length
    });

    return criteria;
  }
);

// ============================================================================
// Legacy Compatibility: RiskFormGenerator (for components not yet migrated)
// Uses state.risks.currentModelId - TO BE REMOVED after full migration
// ============================================================================

// Legacy input selectors - DEPRECATED
const selectCurrentModelId = (state) => state.risks?.currentModelId;
const selectModels = (state) => state.risks?.models || {};

/**
 * @deprecated Use createRiskFormSelector(assessmentUuid) instead
 * This selector relies on legacy state.risks.currentModelId
 */
export const RiskFormGenerator = createSelector(
  [selectCurrentModelId, selectModels, selectTemplatesById, selectLegacyRiskSources, selectLegacyRiskRootCauses],
  (currentModelId, models, templates, riskSources, riskRootCauses) => {
    let finalRiskSources = riskSources;
    let finalRiskRootCauses = riskRootCauses;

    // Check if current model has a template
    if (currentModelId && models[currentModelId]) {
      const model = models[currentModelId];
      const templateId = model.template_id;

      if (templateId && templates[templateId]) {
        const template = templates[templateId];
        finalRiskSources = getRiskAreasFromTemplate(template);
        finalRiskRootCauses = getRootCausesFromTemplate(template);
        console.log('[RiskFormGenerator:LEGACY] Using template:', { currentModelId, templateId });
      }
    }

    if (!finalRiskSources || !finalRiskRootCauses) {
      console.warn('[RiskFormGenerator:LEGACY] No risk sources available');
      return {};
    }

    const riskForm = {};
    Object.keys(finalRiskSources).forEach((category) => {
      const sources = finalRiskSources[category]?.riskSources;
      if (!sources || !Array.isArray(sources)) return;

      riskForm[category] = {
        riskSources: sources.map((source) => ({
          ...source,
          causes: { ...finalRiskRootCauses },
        })),
      };
    });

    return riskForm;
  }
);

// ============================================================================
// Factory Selector: createSecurityImpactCategoriesSelector(assessmentUuid)
// Returns security impact categories (CIA) from assessment's template
// ============================================================================

/**
 * Creates a selector for security impact categories for a specific assessment.
 * Fetches categories from template.taxonomy.securityImpactCategories.
 *
 * @param {string} assessmentUuid - The assessment UUID
 * @returns {Object} Security impact categories: { confidentiality: [...], integrity: [...], availability: [...] }
 */
export const createSecurityImpactCategoriesSelector = (assessmentUuid) => createSelector(
  [selectAssessmentsById, selectTemplatesById],
  (assessments, templates) => {
    // Get the assessment
    const assessment = assessments[assessmentUuid];
    if (!assessment) {
      console.log(`[SecurityImpactCategories] Assessment not found: ${assessmentUuid}`);
      return {};
    }

    // Get template
    const templateId = assessment.info?.template_id;
    const template = templateId ? templates[templateId] : null;

    if (!template) {
      console.log(`[SecurityImpactCategories] No template found for assessment: ${assessmentUuid}`);
      return {};
    }

    const categories = template.taxonomy?.securityImpactCategories;
    if (!categories) {
      console.log(`[SecurityImpactCategories] No security impact categories in template:`, templateId);
      return {};
    }

    console.log(`[SecurityImpactCategories] Using template categories:`, {
      assessmentUuid,
      templateId,
      keys: Object.keys(categories)
    });

    return categories;
  }
);

/**
 * @deprecated Use createImpactCategoriesSelector(assessmentUuid) instead
 */
export const selectImpactCategories = createSelector(
  [selectCurrentModelId, selectModels, selectTemplatesById, selectGlobalConfigs],
  (currentModelId, models, templates, globalConfigs) => {
    const legacyThemes = globalConfigs?.legacy_risk_themes || [];

    if (currentModelId && models[currentModelId]) {
      const templateId = models[currentModelId].template_id;
      if (templateId && templates[templateId]) {
        return getImpactCategoriesFromTemplate(templates[templateId], legacyThemes);
      }
    }

    return legacyThemes;
  }
);
